import React from 'react';
import { Card, Col, Row, Typography } from 'antd';
import type {
  ProposalFormPayload,
  ProposalFormField,
  ProposalFormCategory
} from '../../types/proposal';
import { ProposalFormSection } from './ProposalFormSection';

const { Text } = Typography;

interface ProposalFormProps {
  payload: ProposalFormPayload;
  readOnly?: boolean;
  onChange?: (payload: ProposalFormPayload) => void;
}

const updateField = (
  payload: ProposalFormPayload,
  fieldId: string,
  updates: Partial<ProposalFormField>
): ProposalFormPayload => {
  return {
    ...payload,
    categories: payload.categories.map(category => ({
      ...category,
      fields: category.fields.map(field =>
        field.id === fieldId
          ? {
              ...field,
              ...updates
            }
          : field
      )
    }))
  };
};

const createCustomField = (label: string, value: number | null): ProposalFormField => {
  const idSuffix = Math.random().toString(36).slice(2, 8);
  return {
    id: `custom_${Date.now()}_${idSuffix}`,
    label,
    type: 'flat',
    required: false,
    quantity: 1,
    value: typeof value === 'number' ? value : null,
    note: null,
    isCustom: true,
  };
};

const getFieldTotal = (field: ProposalFormField): number => {
  const rawValue = field.value;
  if (rawValue === null || typeof rawValue === 'undefined') return 0;
  const value = Number(rawValue);
  return Number.isFinite(value) ? value : 0;
};

const getCategorySubtotal = (category: ProposalFormCategory): number => {
  const fields = Array.isArray(category.fields) ? category.fields : [];
  return fields.reduce((total, field) => total + getFieldTotal(field), 0);
};

export const ProposalForm: React.FC<ProposalFormProps> = ({ payload, readOnly, onChange }) => {
  const categories = Array.isArray(payload.categories) ? payload.categories : [];
  const currency = payload.currency || 'USD';
  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0
    }).format(value);
  const categoriesWithTotals = categories.map(category => ({
    category,
    subtotal: getCategorySubtotal(category)
  }));
  const total = categoriesWithTotals.reduce((sum, item) => sum + item.subtotal, 0);

  return (
    <div>
      {categoriesWithTotals.map(({ category, subtotal }) => (
        <ProposalFormSection
          key={category.id}
          category={category}
          subtotal={subtotal}
          formatCurrency={formatCurrency}
          readOnly={readOnly}
          onAddCustomField={(label, value) => {
            if (!onChange) return;
            const newField = createCustomField(label, value);
            onChange({
              ...payload,
              categories: payload.categories.map(item =>
                item.id === category.id
                  ? {
                      ...item,
                      fields: [...item.fields, newField]
                    }
                  : item
              )
            });
          }}
          onFieldChange={(field: ProposalFormField, value) => {
            if (!onChange) return;
            onChange(updateField(payload, field.id, { value }));
          }}
          onFieldLabelChange={(field: ProposalFormField, label: string) => {
            if (!onChange) return;
            onChange(updateField(payload, field.id, { label }));
          }}
          onFieldNoteChange={(field: ProposalFormField, note: string | null) => {
            if (!onChange) return;
            onChange(updateField(payload, field.id, { note }));
          }}
        />
      ))}
      <Card size="small" style={{ marginTop: 16 }}>
        <Row justify="end" align="middle">
          <Col>
            <Text type="secondary" style={{ marginRight: 12 }}>
              Total
            </Text>
          </Col>
          <Col>
            <Text strong>{formatCurrency(total)}</Text>
          </Col>
        </Row>
      </Card>
    </div>
  );
};
